<template>
    <div class="sidebar-content">
        <div class="sidebar-logo">
            <a href="/">
                <img v-if="!states.editing.logo" :src="logo">
            </a>
            <picture-input
                v-if="states.editing.logo"
                id="logo"
                ref="pictureInput"
                :removable="false"
                :plain="true"
                :crop="false"
                size="4"
                name="logo"
                button-class="btn btn-green"
                @change="updateLogo"
            />
            <button v-if="editable" class="inline-edit sidebar-edit" @click.prevent="editLogo">
                <i class="fa fa-pencil"/> Edit logo
            </button>
        </div>
        <div class="sidebar-info">
            <div class="sidebar-heading">
                <h4>Park with {{ companyName }}</h4>
            </div>
            <hr>
            <div class="sidebar-textcontent">
                <div v-if="!platformData.intro && !states.editing.description" class="intro-text">
                    <p>This is where you can enter your own unique description for your brand-new online booking platform.</p>
                    <p>Your visitors are just a few clicks away from being able to search and book stress-free parking - leaving you with nothing more to do other than to watch the bookings roll in!</p>
                </div>
                <template v-if="states.editing.description">
                    <textarea
                        id="editDescription"
                        :disabled="states.submitting"
                        v-model="platformData.intro"
                        rows="8"
                    />
                    <button
                        v-if="editable"
                        :disabled="states.submitting"
                        class="inline-edit sidebar-edit-confirm"
                        @click.prevent="saveDescription"
                    >
                        <i class="fa fa-check"/>Save description
                    </button>
                </template>
                <template v-else>
                    <div v-for="paragraph in descriptionParagraphs">
                        <template v-if="typeof paragraph === 'string'">
                            {{ paragraph }}
                        </template>
                        <template v-else>
                            <p v-for="subp in paragraph">
                                {{ subp }}
                            </p>
                        </template>
                    </div>
                    <button v-if="editable" class="inline-edit sidebar-edit" @click.prevent="editDescription">
                        <i class="fa fa-pencil"/>Edit description
                    </button>
                </template>
            </div>
        </div>
        <div class="sidebar-contact">
            <template v-if="!states.editing.details">
                <ul>
                    <li v-if="platformData.phone" class="contact-phone">
                        <a :href="phoneUrl">{{ platformData.phone }}</a>
                    </li>
                    <li v-if="platformData.email" class="contact-email">
                        <a :href="emailUrl">{{ platformData.email }}</a>
                    </li>
                    <li v-if="platformData.website_url" class="contact-www">
                        <a :href="siteUrl" target="_blank">{{ beautyWebsiteUrl }}</a>
                    </li>
                    <li v-if="platformData.facebook" class="contact-facebook">
                        <a :href="facebookUrl" target="_blank">Facebook</a>
                    </li>
                    <li v-if="platformData.twitter" class="contact-twitter">
                        <a :href="twitterUrl" target="_blank">Twitter</a>
                    </li>
                    <li v-if="isHorizonParking" class="contact-info">
                        <a href="/frequently-asked-questions">Frequently asked questions</a>
                    </li>
                </ul>
                <button v-if="editable" class="inline-edit sidebar-edit-confirm" @click.prevent="editDetails">
                    <i class="fa fa-pencil"/>Edit details
                </button>
            </template>
            <template v-else>
                <ul>
                    <li class="contact-phone">
                        <input v-model="platformData.phone" :disabled="states.submitting" type="tel">
                    </li>
                    <li class="contact-email">
                        <input v-model="platformData.email" :disabled="states.submitting" type="email">
                    </li>
                    <li class="contact-www">
                        <input v-model="platformData.website_url" :disabled="states.submitting" type="url">
                    </li>
                    <li class="contact-facebook">
                        <input v-model="platformData.facebook" :disabled="states.submitting" type="text">
                    </li>
                    <li class="contact-twitter">
                        <input v-model="platformData.twitter" :disabled="states.submitting" type="text">
                    </li>
                </ul>
                <button
                    v-if="editable"
                    :disabled="states.submitting"
                    class="inline-edit sidebar-edit-confirm"
                    @click.prevent="saveDetails"
                >
                    <i class="fa fa-check"/>Save details
                </button>
            </template>
        </div>
        <div v-if="searchHomepageEnabled" class="controls">
            <a href="/">
                <button class="btn btn-block btn-primary">
                    <i class="fa fa-reply"/> Back To Search
                </button>
            </a>
            <a v-if="searchResultsShown" href="/listings">
                <button class="btn btn-block btn-primary">
                    All Locations
                </button>
            </a>
        </div>
        <div v-if="platformData.termsConditions" class="sidebar-textcontent sidebar-policy">
            <p>
                <a :href="platformData.termsConditions" target="_blank">Our Terms &amp; Conditions</a>
            </p>
        </div>
        <div v-if="platformData.privacyPolicy" class="sidebar-textcontent sidebar-policy">
            <p>
                <a :href="platformData.privacyPolicy" target="_blank">Our Privacy Policy</a>
            </p>
        </div>
    </div>
</template>

<script>
    import WhitelabelRepository from '../../repositories/WhitelabelRepository';
    import { getViewData } from '../../modules/view-data';
    import queryString from 'query-string';

    export default {
        name: 'sidebar-content',

        props: {
            id: {
                type: Number,
                required: true,
            },

            space: {
                type: Object,
                default() {
                    return {};
                },
            },

            editable: {
                type: Boolean,
                default: false,
            },

            companyName: {
                type: String,
                default: 'YourParkingSpace',
            },

            intro: {
                type: String,
                default: '',
            },

            phone: {
                type: String,
                default: '01234 567890',
            },

            email: {
                type: String,
                default: 'email@example.com',
            },

            website: {
                type: String,
                default: 'www.example.com',
            },

            facebook: {
                type: String,
                default: null,
            },

            twitter: {
                type: String,
                default: null,
            },

            logo: {
                type: String,
                default: '/images/whitelabel/logos/yps.png',
            },

            textColour: {
                type: String,
                default: 'ffffff',
            },

            privacyPolicy: {
                type: String,
                default: null,
            },

            termsConditions: {
                type: String,
                default: null,
            },
        },

        data() {
            return {
                states: {
                    submitting: false,
                    editing: {
                        description: false,
                        details: false,
                        logo: false,
                    },
                },
                platformData: {
                    intro: '',
                    website_url: null,
                    email: null,
                    phone: null,
                    facebook: null,
                    twitter: null,
                    privacyPolicy: null,
                    termsConditions: null,
                },
                errors: [],
            };
        },

        computed: {
            // split description into paragraphs by line break
            descriptionParagraphs() {
                return this.platformData.intro
                    .split(/\n\s*\n/gm)
                    .map((result) => {
                        const paragraphs = result.split(/\n/gm);
                        return paragraphs.length > 1 ? paragraphs : paragraphs[0];
                    });
            },

            // remove the http:// and https:// from website url, because it's ugly and takes space
            beautyWebsiteUrl() {
                return this.platformData.website_url.replace(/https:\/\/|http:\/\//, '');
            },

            phoneUrl() {
                return `tel:${ this.platformData.phone }`;
            },

            emailUrl() {
                return `mailto:${ this.platformData.email }`;
            },

            facebookUrl() {
                return `http://facebook.com/${ this.platformData.facebook }`;
            },

            twitterUrl() {
                return `http://twitter.com/${ this.platformData.twitter }`;
            },

            siteUrl() {
                return `http://${ this.beautyWebsiteUrl }`;
            },

            hexTextColour() {
                return `#${ this.textColour }`;
            },

            searchHomepageEnabled() {
                return getViewData('whitelabel_search', false);
            },

            query() {
                return queryString.parse(window.location.search);
            },

            searchResultsShown() {
                return this.query.lat && this.query.lng;
            },

            isHorizonParking() {
                return this.companyName === 'Horizon Parking';
            },
        },

        mounted() {
            Object.keys(this.platformData).forEach((key) => {
                this.platformData[key] = this[key];

                if (key === 'website_url') {
                    this.platformData[key] = this.website;
                }
            });
        },

        methods: {
            editLogo() {
                this.states.editing.logo = true;
            },

            updateLogo() {
                this.uploadImage(this.$refs.pictureInput.file);
            },

            uploadImage(image) {
                const url = getViewData('CLIENT_API_URL');

                this.$http.put(`${ url }/whitelabel/${ this.id }/logo`, {}, {
                    before(request) {
                        request.body = image;
                    },
                    headers: {
                        'Content-Type': image.type,
                    },
                })
                .then(({ data }) => {
                    this.logo = data.data.logo_url;
                    this.states.editing.logo = false;
                })
                .catch(({ data }) => {
                    this.errors.push(data.message);
                });
            },

            editDescription() {
                this.states.editing.description = true;
            },

            editDetails() {
                this.states.editing.details = true;
            },

            saveDescription() {
                if (this.states.submitting) {
                    return;
                }

                this.states.submitting = true;

                WhitelabelRepository.update(this.id, {
                    intro: this.platformData.intro,
                }).then(() => {
                    this.states.editing.description = false;
                    this.states.submitting = false;
                }).catch(() => {
                    this.states.submitting = false;
                });
            },

            saveDetails() {
                if (this.states.submitting) {
                    return;
                }

                this.states.submitting = true;

                if (this.platformData.website_url && !(/https:\/\/|http:\/\//.test(this.platformData.website_url))) {
                    this.platformData.website_url = `http://${ this.platformData.website_url }`;
                }

                WhitelabelRepository.update(this.id, this.platformData)
                    .then(() => {
                        this.states.editing.details = false;
                        this.states.submitting = false;
                    }).catch(() => {
                        this.states.submitting = false;
                    });
            },
        },
    };
</script>

<style lang="less" scoped>
    @import (reference) "~assets/less/variables";
    @import (reference) "~bootstrap/less/mixins/clearfix.less";

    #sidebar {
        .sidebar-logo {
            .clearfix;
            position: relative;
            padding: 0;
            top: 0;
            margin-bottom: 25px;

            a {
                display: block;
            }

            .inline-edit {
                float: left;
                margin-top: 25px;
            }

            .picture-input {
                background-color: #ffffff;
            }
        }
    }
    button.inline-edit, button.inline-search {
        font-size: 11px;
        color: #40433a;
        font-weight: 600;
        font-family: @headings-font-family;
        background-color: #ffffff;
        box-shadow: 0 0 5px rgba(0, 0, 0, 0.27);
        padding: 5px 8px;
        border: none;
        border-radius: 4px;
        display: inline-block;
        outline: none;
        margin: 30px auto 10px;

        &:disabled {
            opacity: 0.7;
        }
        i {
            margin-right: 5px;
            color: @YPS-secondary;
        }
    }
    .sidebar-textcontent {
        > * {
            color: #ffffff;
            font-size: 14px;
            margin-bottom: 20px;
            line-height: 20px;
            font-weight: 200;

            &:last-child {
                margin-bottom: 0;
            }
        }

        textarea {
            width: 100%;
            resize: none;
            height: auto;
            font-size: 14px;
            color: #3c4246;
            border: 1px solid #979797;
            border-radius: 4px;
            padding: 14px 17px;
            margin-bottom: 10px;
            display: block;
            outline: none;

            &:disabled {
                color: #848484;
                background: #eeeeee;
            }
        }
    }

    .sidebar-content {
        padding-top: 20px;
        width: 112%;
        padding-right: 12%;
        max-height: 100%;
        overflow-y: scroll;
    }

    .sidebar-info {
        text-align: left;

        h4 {
            font-size: 20px;
            color: #ffffff;
            margin-top: 12px;
        }

        h5 {
            font-size: 11px;
            font-weight: 700;
            text-transform: uppercase;
            color: #ffffff;
        }

        hr {
            margin: 27px 0;
            width: 60px;
            opacity: .28;
            border-width: 4px;
            color: #ffffff;
        }
    }

    .sidebar-contact {
        margin-top: 40px;
        margin-bottom: 20px;
        text-align: left;

        .contact-facebook,
        .contact-twitter {
            a {
                text-decoration: underline;
            }
        }

        ul {
            li {
                input {
                    font-size: 15px;
                    color: #3c4246;
                    padding: 7px 12px;
                    width: 100%;
                    border-radius: 4px;
                    border: none;
                    outline: none;
                    background: #ffffff;

                    &:disabled {
                        color: #848484;
                        background: #eeeeee;
                    }
                }
            }
        }

        button {
            margin-top: 10px;
        }

        ul {
            list-style: none;
            text-align: left;
            margin: 0;
            padding: 0;

            li {
                background-repeat: no-repeat;
                background-position: center left;
                padding: 6px 0 6px 42px;
                position: relative;
                color: #ffffff;

                &:before {
                    display: block;
                    position: absolute;
                    left: 0;
                    width: 34px;
                    height: 34px;
                    background: #2e8fb6;
                    font-family: FontAwesome;
                    color: #ffffff;
                    border-radius: 100%;
                    text-align: center;
                    line-height: 34px;
                    font-weight: normal;
                    font-size: 14px;
                }

                &.contact-phone:before {
                    content: '\f095';
                }
                &.contact-email:before {
                    content: '\f0e0';
                }
                &.contact-www:before {
                    content: '\f0ac';
                }
                &.contact-facebook:before {
                    content: '\f09a';
                }
                &.contact-twitter:before {
                    content: '\f099';
                }
                &.contact-info:before {
                    content: '\f129';
                }
                a {
                    color: #ffffff;
                    font-weight: 200;
                    font-size: 14px;
                    line-height: 34px;
                }
            }
        }
    }

    .controls {
        margin-top: 46px;
        button {
            margin-bottom: 12px;
        }
    }

    .sidebar-credits {
        padding: 20px;
        color: #ffffff;

        hr {
            margin-bottom: 30px;
            width: 33px;
            border-width: 2px;
        }

        span {
            text-transform: uppercase;
            font-size: 12px;
            font-weight: 700;
            margin-bottom: 4px;
        }
    }
</style>
